import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { homeSchemas } from '../lib/schemas';

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 3.5rem 2rem 5.5rem;
  position: relative;
  overflow: hidden;
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroImage = styled.img`
  width: 45%;
  max-width: 480px;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.3);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 90%;
    max-width: 100%;
  }
`;

const HeroContent = styled.div`
  flex: 1;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 2.8rem;
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 1.25rem;
  font-family: var(--font-heading);
  white-space: nowrap;

  @media (max-width: 900px) {
    white-space: normal;
    font-size: 2rem;
  }
`;

const HeroTagline = styled.p`
  color: rgba(255, 255, 255, 0.92);
  font-size: 1.15rem;
  line-height: 1.75;
  margin: 0;
  font-style: italic;
`;

/* ─────────────────────────────────────────
   LEAD (below hero)
───────────────────────────────────────── */
const LeadSection = styled.section`
  background-color: #fff;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
`;

const LeadButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  background-color: var(--primary-color);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.85rem 2rem;
  border-radius: 100px;
  transition: background-color 0.2s, transform 0.15s;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background-color: #5a8a87;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Link)`
  background-color: transparent;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 1rem;
  padding: 0.85rem 2rem;
  border-radius: 100px;
  border: 2px solid var(--primary-color);
  transition: border-color 0.2s, background-color 0.2s, transform 0.15s;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background-color: rgba(122,166,162,0.08);
    transform: translateY(-1px);
  }
`;

/* Wave SVG at bottom of hero */
const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;

  svg {
    width: 100%;
    height: 64px;
    display: block;
  }
`;

/* ─────────────────────────────────────────
   SOCIAL PROOF STRIP
───────────────────────────────────────── */
const ProofSection = styled.section`
  background-color: #f5f3f1;
  padding: 2.25rem 2rem;
  text-align: center;
  border-bottom: 1px solid #e8e5e2;
`;

const ProofLabel = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #aaa;
  margin-bottom: 1.25rem;
  font-weight: 600;
`;

const ProofLogos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 2rem;
  justify-content: center;
  align-items: center;
`;

const ProofName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #999;
  white-space: nowrap;
`;

/* ─────────────────────────────────────────
   STATS STRIP
───────────────────────────────────────── */
const StatsSection = styled.section`
  background-color: #fff;
  padding: 3.5rem 2rem;
  border-bottom: 1px solid #eee;
`;

const StatsGrid = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatNumber = styled.div`
  font-family: var(--font-heading);
  font-size: 3rem;
  font-weight: 700;
  color: var(--accent-color);
  line-height: 1;
  margin-bottom: 0.4rem;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #888;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

/* ─────────────────────────────────────────
   PILLARS
───────────────────────────────────────── */
const PillarsSection = styled.section`
  padding: 5.5rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--accent-color);
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.25;
  font-family: var(--font-heading);
`;

const SectionSubtitle = styled.p`
  color: #777;
  font-size: 1.05rem;
  max-width: 580px;
  margin: 0 auto 3.5rem;
  line-height: 1.75;
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.75rem;
  text-align: left;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PillarCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 2.25rem;
  border: 1px solid #eee;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0,0,0,0.09);
  }
`;

const PillarIcon = styled.div`
  font-size: 2.25rem;
  margin-bottom: 1.1rem;
`;

const PillarTitle = styled.h3`
  color: #333;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  font-family: var(--font-heading);
`;

const PillarText = styled.p`
  color: #666;
  font-size: 0.98rem;
  line-height: 1.75;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const PillarLink = styled(Link)`
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.92rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  &:hover {
    color: #5a8a87;
    opacity: 1;
  }
`;

/* ─────────────────────────────────────────
   SCRIPTURE PULL QUOTE
───────────────────────────────────────── */
const QuoteSection = styled.section`
  background-color: #333;
  padding: 5rem 2rem;
  text-align: center;
`;

const QuoteMark = styled.div`
  font-family: var(--font-heading);
  font-size: 5rem;
  color: var(--accent-color);
  line-height: 0.5;
  margin-bottom: 1.5rem;
  opacity: 0.8;
`;

const QuoteText = styled.blockquote`
  font-family: var(--font-heading);
  font-style: italic;
  font-size: 1.75rem;
  color: #fff;
  max-width: 720px;
  margin: 0 auto 1.25rem;
  line-height: 1.5;
  font-weight: 400;

  @media (max-width: 600px) {
    font-size: 1.35rem;
  }
`;

const QuoteSource = styled.p`
  color: var(--accent-color);
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
`;

/* ─────────────────────────────────────────
   FOUNDERS
───────────────────────────────────────── */
const FoundersSection = styled.section`
  background-color: #FAF9F8;
  padding: 5.5rem 2rem;
`;

const FoundersInner = styled.div`
  max-width: 920px;
  margin: 0 auto;
  text-align: center;
`;

const FoundersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 2.5rem;
  margin-top: 3.5rem;
  text-align: left;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FounderCard = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
`;

const FounderPhoto = styled.div`
  width: 68px;
  height: 68px;
  min-width: 68px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #7AA6A2 0%, #a8c8c5 100%);
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FounderInfo = styled.div``;

const FounderName = styled.h3`
  color: #333;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.15rem;
  font-family: var(--font-heading);
`;

const FounderRole = styled.p`
  color: var(--accent-color);
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FounderBio = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.7;
`;

/* ─────────────────────────────────────────
   TESTIMONIALS
───────────────────────────────────────── */
const TestimonialsSection = styled.section`
  background-color: #fff;
  padding: 5.5rem 2rem;
`;

const TestimonialsInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
`;

const TestimonialsGrid = styled.div`
  display: flex;
  align-items: stretch;
  gap: 2rem;
  margin-top: 3.5rem;
  text-align: left;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const TestimonialCard = styled.div`
  flex: 1;
  background: #FAF9F8;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #eee;
  box-sizing: border-box;
  display: grid;
  grid-template-rows: 1fr auto auto;
`;

const TestimonialDivider = styled.div`
  border-top: 1px solid #e8e5e2;
  margin-top: 1.25rem;
`;

const TestimonialQuote = styled.p`
  color: #444;
  font-size: 1rem;
  line-height: 1.8;
  font-style: italic;
  margin: 0 0 1.25rem;

  &::before {
    content: '“';
    font-family: var(--font-heading);
    font-size: 2.5rem;
    color: var(--primary-color);
    line-height: 0;
    vertical-align: -0.75rem;
    margin-right: 0.15rem;
    opacity: 0.5;
  }

  &::after {
    content: '”';
    font-family: var(--font-heading);
    font-size: 2.5rem;
    color: var(--primary-color);
    line-height: 0;
    vertical-align: -0.75rem;
    margin-left: 0.15rem;
    opacity: 0.5;
  }
`;

const TestimonialAuthor = styled.div`
  padding-top: 1rem;
`;

const TestimonialName = styled.p`
  color: #333;
  font-weight: 700;
  font-size: 0.92rem;
  margin: 0 0 0.15rem;
`;

const TestimonialRole = styled.p`
  color: #7AA6A2;
  font-size: 0.82rem;
  font-weight: 600;
  margin: 0;
  min-height: 1.2em;
`;

/* ─────────────────────────────────────────
   BOTTOM CTA
───────────────────────────────────────── */
const CtaSection = styled.section`
  background-color: #7AA6A2;
  padding: 5.5rem 2rem;
  text-align: center;
`;

const CtaTitle = styled.h2`
  color: #fff;
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-heading);
`;

const CtaText = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 1.1rem;
  max-width: 520px;
  margin: 0 auto 2.25rem;
  line-height: 1.75;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: #7AA6A2;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.9rem 2.25rem;
  border-radius: 100px;
  transition: background-color 0.2s, transform 0.15s;
  text-decoration: none;

  &:hover {
    background-color: #f0f8f7;
    color: #5a8a87;
    transform: translateY(-1px);
  }
`;

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const PROOF_VENUES = [
  'TEDxVNUIS', 'HarvestNet International', 'HackerNoon',
  'Saigon A.I.', 'RMIT', 'UEH', 'LaunchJDI', 'She Loves Data',
  'Rawlinsville Camp Meeting', 'Indigenous Ministry Links',
];

const STATS = [
  { number: '30+', label: 'Years Experience' },
  { number: '8',   label: 'Startups Founded' },
  { number: '16+', label: 'Speaking Events' },
  { number: '4',   label: 'Continents' },
];

const TESTIMONIALS = [
  {
    quote: "I'm excited to hear about Building on Truth! Andrew and Rebecca have great hearts for the LORD and are living out their faith by giving care to those they meet along the journey.",
    name: 'Pastor Blake Deibler',
    role: 'Senior Pastor · Wesley Network',
  },
  {
    quote: "Presents a knowledgeable presentation that brings clarity on what AI is and where it's headed — from a Christian perspective.",
    name: 'Janice Kaufman',
    role: '',
  },
];

const PILLARS = [
  {
    icon: '🤖',
    title: 'AI & The Christian Life',
    text: 'How should Christians think about artificial intelligence? We provide theological foundations, ethical frameworks, and practical wisdom for engaging AI in ways that honor God and serve humanity.',
    link: '/workshops',
    linkLabel: 'Explore the workshop →',
  },
  {
    icon: '🏗️',
    title: 'Entrepreneurship as a Calling',
    text: 'God wires many believers with entrepreneurial gifts. We help you understand your calling, build on biblical principles, and lead with integrity in competitive markets.',
    link: '/workshops',
    linkLabel: 'Explore the workshop →',
  },
  {
    icon: '🌍',
    title: 'Business as a Mission',
    text: "Your company, your career, your marketplace calling — these aren't separate from ministry. They are ministry. We help believers see business as a primary platform for gospel witness.",
    link: '/workshops',
    linkLabel: 'Explore the workshop →',
  },
  {
    icon: '💍',
    title: 'Premarital Counseling',
    text: 'Many couples enter marriage carrying hidden struggles, unspoken fears, and unresolved patterns. We help couples build marriages on honesty, grace, and biblical covenant from the very start.',
    link: '/workshops',
    linkLabel: 'Explore the workshop →',
  },
];

const Home = () => {
  return (
    <Layout
      title="Home"
      description="Equipping Christians to engage AI, entrepreneurship, and business as a mission with biblical faithfulness and kingdom purpose."
      path="/"
      image="/img/og-default.jpg"
      imageAlt="Building on Truth — Where Faith Meets Innovation"
      keywords="Christian AI, faith and technology, business as mission, ministry incubators, Christian entrepreneurship, Andrew Schwabe, Rebecca Schwabe"
      jsonLd={homeSchemas()}
    >
      {/* ── Hero ── */}
      <HeroSection>
        <HeroInner>
          <HeroImage src="/img/home_tower_buildingontruth.jpg" alt="Building on Truth" />
          <HeroContent>
            <HeroTitle>Where Faith Meets Innovation</HeroTitle>
            <HeroTagline>
              Equipping Christians to navigate A.I., ethics, entrepreneurship skills, innovation, and business as a mission (BAM) with biblical&nbsp;wisdom
            </HeroTagline>
          </HeroContent>
        </HeroInner>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#fff" />
          </svg>
        </HeroWave>
      </HeroSection>

      {/* ── Lead ── */}
      <LeadSection>
        <LeadButtons>
          <PrimaryButton to="/workshops">See Our Workshops</PrimaryButton>
          <SecondaryButton to="/contact">Book a Free Call</SecondaryButton>
        </LeadButtons>
      </LeadSection>

      {/* ── Social proof ── */}
      <ProofSection>
        <ProofLabel>Trusted by organizations around the world</ProofLabel>
        <ProofLogos>
          {PROOF_VENUES.map((v) => <ProofName key={v}>{v}</ProofName>)}
        </ProofLogos>
      </ProofSection>

      {/* ── Stats ── */}
      <StatsSection>
        <StatsGrid>
          {STATS.map((s) => (
            <div key={s.label}>
              <StatNumber>{s.number}</StatNumber>
              <StatLabel>{s.label}</StatLabel>
            </div>
          ))}
        </StatsGrid>
      </StatsSection>

      {/* ── Three pillars ── */}
      <PillarsSection>
        <SectionLabel>What We Do</SectionLabel>
        <SectionTitle>Biblical wisdom for a changing world</SectionTitle>
        <SectionSubtitle>
          Four areas where faith and life collide — and where we believe Christians are uniquely called to lead.
        </SectionSubtitle>
        <PillarsGrid>
          {PILLARS.map((p) => (
            <PillarCard key={p.title}>
              <PillarIcon>{p.icon}</PillarIcon>
              <PillarTitle>{p.title}</PillarTitle>
              <PillarText>{p.text}</PillarText>
              <PillarLink to={p.link}>{p.linkLabel}</PillarLink>
            </PillarCard>
          ))}
        </PillarsGrid>
      </PillarsSection>

      {/* ── Scripture pull quote ── */}
      <QuoteSection>
        <QuoteMark>"</QuoteMark>
        <QuoteText>
          Whatever you do, work at it with all your heart, as working for the Lord, not for human masters.
        </QuoteText>
        <QuoteSource>Colossians 3:23</QuoteSource>
      </QuoteSection>

      {/* ── Founders ── */}
      <FoundersSection>
        <FoundersInner>
          <SectionLabel>Who We Are</SectionLabel>
          <SectionTitle>Practitioners, not theorists</SectionTitle>
          <SectionSubtitle>
            Andrew and Rebecca bring decades of real-world experience building technology companies across the US, Vietnam, and beyond — and a conviction that faith belongs at the center of it all.
          </SectionSubtitle>
          <FoundersGrid>
            <FounderCard>
              <FounderPhoto><img src="/img/andrew_pic.JPG" alt="Andrew Schwabe" /></FounderPhoto>
              <FounderInfo>
                <FounderName>Andrew Schwabe</FounderName>
                <FounderRole>Serial Entrepreneur · AI Researcher · Speaker</FounderRole>
                <FounderBio>
                  30+ years building international tech companies. AI researcher at University of St. Andrews, co-founder of Saigon A.I., and founder of TrustMetrics. Andrew teaches with theological depth and an unshakeable belief that believers can be salt and light in the tech industry.
                </FounderBio>
              </FounderInfo>
            </FounderCard>
            <FounderCard>
              <FounderPhoto><img src="/img/rebecca_pic.png" alt="Rebecca Joy" /></FounderPhoto>
              <FounderInfo>
                <FounderName>Rebecca Joy</FounderName>
                <FounderRole>Tech Entrepreneur · Data Scientist · Speaker</FounderRole>
                <FounderBio>
                  25+ years and six startups including Saigon A.I., where she leads a data science team serving Global Fortune 100 clients. UC Berkeley educated, Rebecca is a passionate advocate for women in technology across Southeast Asia.
                </FounderBio>
              </FounderInfo>
            </FounderCard>
          </FoundersGrid>
        </FoundersInner>
      </FoundersSection>

      {/* ── Testimonials ── */}
      <TestimonialsSection>
        <TestimonialsInner>
          <SectionLabel>What People Say</SectionLabel>
          <SectionTitle>Voices from the field</SectionTitle>
          <TestimonialsGrid>
            {TESTIMONIALS.map((t) => (
              <TestimonialCard key={t.name}>
                <TestimonialQuote>{t.quote}</TestimonialQuote>
                <TestimonialDivider />
                <TestimonialAuthor>
                  <TestimonialName>{t.name}</TestimonialName>
                  <TestimonialRole>{t.role}</TestimonialRole>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </TestimonialsInner>
      </TestimonialsSection>

      {/* ── Bottom CTA ── */}
      <CtaSection>
        <CtaTitle>Ready to bring this to your community?</CtaTitle>
        <CtaText>
          Whether you're hosting a conference, a church retreat, or a corporate event — we'd love to talk about how we can serve your audience.
        </CtaText>
        <CtaButton to="/events">Request a Speaker</CtaButton>
      </CtaSection>
    </Layout>
  );
};

export default Home;
